import { decodeFunctionResult, encodeFunctionData, encodeFunctionResult, getAddress, isAddress, keccak256,
  zeroAddress, type Abi, type Address, type Hex } from "viem";
import type { BootstrapResponse } from "../api/schemas";
import { isProductionRedeemExecution } from "../api/action-capabilities";
import { ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import { creatorFeeCurveAbi, creatorFeeReceiverAbi, creatorFeeVaultAbi } from "../contracts/creator-fees";
import { contractReadError } from "../contracts/contract-errors";
import type { Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadOptions } from "./read-rpc";
import type { RedeemFeeRoute } from "./redeem-fee-quote";

// ERC-1967 implementation slot, not a runtime deployment address.
const IMPLEMENTATION_SLOT = "0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc";
export type RedeemDeploymentObservation = Readonly<{
  collectionCodeHash: Hex; implementation: Address | null; implementationCodeHash: Hex | null;
}>;
type BlockReference = Hex | "latest" | { blockHash: Hex; requireCanonical: true };
class RedeemRouteError extends Error {}
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** Admission is bootstrap authority, separate from observed contract fingerprints. */
export function isProductionRedeemRouteEnabled(route: RedeemFeeRoute, bootstrap: BootstrapResponse | null): boolean {
  if (!isProductionRedeemExecution(bootstrap) || !bootstrap || route.kind !== "REDEEM_WITH_FEES_CURVE_V1" ||
    route.support !== "PINNED_RPC_READ_ONLY" || route.environment !== "production" ||
    !route.observedDeployment || !route.deploymentRevision.trim()) return false;
  const token = bootstrap.tokens.find(item => same(item.tokenAddress, route.sourceToken));
  return Boolean(token && token.chainId === route.chainId && route.chainId === bootstrap.contracts.mintChainId &&
    route.chainId === bootstrap.deployment.executionChainId && token.curveAddress && same(token.curveAddress, route.curve) &&
    bootstrap.contracts.collectionAddress && same(bootstrap.contracts.collectionAddress, route.collection) &&
    bootstrap.contracts.rewardVaultAddress && same(bootstrap.contracts.rewardVaultAddress, route.vault) &&
    (!bootstrap.contracts.dedicatedFeeReceiverAddress || same(bootstrap.contracts.dedicatedFeeReceiverAddress, route.receiver)));
}
function address(value: unknown): Address {
  if (typeof value !== "string" || !isAddress(value, { strict: false }) || same(value, zeroAddress)) {
    throw new RedeemRouteError("Invalid redemption route address");
  }
  return getAddress(value);
}
function header(value: unknown) {
  const h = value && typeof value === "object" ? value as Record<string, unknown> : {};
  if (typeof h.number !== "string" || !/^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(h.number) ||
    typeof h.hash !== "string" || !/^0x[0-9a-f]{64}$/i.test(h.hash) || BigInt(h.hash) === 0n ||
    typeof h.timestamp !== "string" || !/^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(h.timestamp)) {
    throw new RedeemRouteError("Invalid redemption route block");
  }
  return { number: h.number as Hex, hash: h.hash as Hex, timestamp: h.timestamp };
}

/** Fingerprints an observation; it does not certify method support or grant send permission. */
export async function observeRedeemDeployment(provider: Eip1193Provider, collection: Address,
  reference: BlockReference, assertCurrent: () => void, options: RpcReadOptions = {}): Promise<RedeemDeploymentObservation> {
  const guard = () => { options.signal?.throwIfAborted(); assertCurrent(); };
  const request = async (method: "eth_getCode" | "eth_getStorageAt", params: readonly unknown[]) => {
    guard();
    const result = await readRpc(provider, { method, params }, options);
    guard();
    return result;
  };
  const codeHash = (code: unknown): Hex => {
    if (typeof code !== "string" || !/^0x(?:[0-9a-f]{2})+$/i.test(code)) throw new RedeemRouteError("Redemption deployment has no valid code");
    return keccak256(code as Hex);
  };
  const [code, slot] = await Promise.all([
    request("eth_getCode", [collection, reference]), request("eth_getStorageAt", [collection, IMPLEMENTATION_SLOT, reference]),
  ]);
  if (typeof slot !== "string" || !/^0x[0-9a-f]{64}$/i.test(slot) || !/^0x0{24}/i.test(slot)) {
    throw new RedeemRouteError("Invalid ERC-1967 implementation storage");
  }
  const collectionCodeHash = codeHash(code);
  const implementation = BigInt(slot) === 0n ? null : address(`0x${slot.slice(-40)}`);
  const implementationCodeHash = implementation ? codeHash(await request("eth_getCode", [implementation, reference])) : null;
  guard();
  return Object.freeze({ collectionCodeHash, implementation, implementationCodeHash });
}

/** Resolve configured source targets at one canonical hash pin. Read-only in every environment. */
export async function resolveRedeemFeeRoute(provider: Eip1193Provider, bootstrap: BootstrapResponse,
  sourceToken: string, assertCurrent: () => void, options: RpcReadOptions = {}): Promise<RedeemFeeRoute> {
  const guard = () => { options.signal?.throwIfAborted(); assertCurrent(); };
  guard();
  const token = bootstrap.tokens.find(item => same(item.tokenAddress, sourceToken));
  const chainId = bootstrap.contracts.mintChainId;
  if (!token || !chainId || token.chainId !== chainId || chainId !== bootstrap.deployment.executionChainId ||
    bootstrap.deployment.sourceTokenStatus !== "REGISTERED" ||
    ["NOT_DEPLOYED", "NOT_CONFIGURED"].includes(bootstrap.deployment.projectContractsStatus)) {
    throw new RedeemRouteError("Configured source and redemption deployment are unavailable");
  }
  const collection = address(bootstrap.contracts.collectionAddress), curve = address(token.curveAddress);
  const source = address(sourceToken), configuredVault = address(bootstrap.contracts.rewardVaultAddress);
  const request = async (method: "eth_chainId" | "eth_getBlockByNumber" | "eth_call", params: readonly unknown[]) => {
    guard();
    const result = await readRpc(provider, { method, params }, options);
    guard();
    return result;
  };
  const checkChain = async () => {
    const chain = await request("eth_chainId", []);
    if (typeof chain !== "string" || !/^0x[0-9a-f]+$/i.test(chain) || BigInt(chain) !== BigInt(chainId)) {
      throw new RedeemRouteError("Select the execution network before quoting redemption");
    }
  };
  try {
    await checkChain();
    const pin = header(await request("eth_getBlockByNumber", ["latest", false]));
    const reference = { blockHash: pin.hash, requireCanonical: true } as const;
    const read = async (to: Address, abi: Abi, functionName: string, args: readonly unknown[] = []) => {
      const raw = await request("eth_call", [{ to, data: encodeFunctionData({ abi, functionName, args }) }, reference]);
      if (typeof raw !== "string" || !/^0x(?:[0-9a-f]{2})+$/i.test(raw)) throw new RedeemRouteError("Invalid redemption binding response");
      const result = decodeFunctionResult({ abi, functionName, data: raw as Hex });
      if (encodeFunctionResult({ abi, functionName, result }).toLowerCase() !== raw.toLowerCase()) {
        throw new RedeemRouteError("Noncanonical redemption binding response");
      }
      return result;
    };
    const [vaultRaw, instanceRaw, tokenRaw, factoryRaw, escrowRaw, recipientRaw, observedDeployment] = await Promise.all([
      read(collection, ponsRewardNftAbi, "FEE_VAULT"), read(collection, ponsRewardNftAbi, "INSTANCE_ID"),
      read(curve, creatorFeeCurveAbi, "token"), read(curve, creatorFeeCurveAbi, "factory"),
      read(curve, creatorFeeCurveAbi, "feeEscrow"), read(curve, creatorFeeCurveAbi, "deployer"),
      observeRedeemDeployment(provider, collection, reference, guard, options),
    ]);
    const vault = address(vaultRaw), factory = address(factoryRaw), escrow = address(escrowRaw);
    const receiver = address(recipientRaw);
    if (!same(vault, configuredVault) || !same(address(tokenRaw), source) ||
      bootstrap.contracts.dedicatedFeeReceiverAddress && !same(receiver, bootstrap.contracts.dedicatedFeeReceiverAddress) ||
      typeof instanceRaw !== "string" || !/^0x[0-9a-f]{64}$/i.test(instanceRaw) || BigInt(instanceRaw) === 0n) {
      throw new RedeemRouteError("Configured source/curve/vault/receiver binding mismatch");
    }
    const [controller, vaultReceiver, receiverSource, receiverVault, receiverEscrow, receiverInstance] = await Promise.all([
      read(vault, creatorFeeVaultAbi, "redemptionController"), read(vault, creatorFeeVaultAbi, "sourceFeeReceiver", [source]),
      read(receiver, creatorFeeReceiverAbi, "SOURCE_TOKEN"), read(receiver, creatorFeeReceiverAbi, "FEE_VAULT"),
      read(receiver, creatorFeeReceiverAbi, "UPSTREAM_ESCROW"), read(receiver, creatorFeeReceiverAbi, "INSTANCE_ID"),
    ]);
    if (!same(address(controller), collection) || !same(address(vaultReceiver), receiver) ||
      !same(address(receiverSource), source) || !same(address(receiverVault), vault) ||
      !same(address(receiverEscrow), escrow) || typeof receiverInstance !== "string" || !same(receiverInstance, instanceRaw)) {
      throw new RedeemRouteError("Redemption controller/receiver binding mismatch");
    }
    const recheck = header(await request("eth_getBlockByNumber", [pin.number, false]));
    if (!same(pin.hash, recheck.hash) || pin.number !== recheck.number || pin.timestamp !== recheck.timestamp) {
      throw new RedeemRouteError("Redemption route snapshot changed");
    }
    await checkChain(); guard();
    // Fingerprints and derived addresses fence upgrades without claiming verified source semantics.
    const deploymentRevision = JSON.stringify([chainId, collection, source, curve, vault, receiver, factory, escrow,
      instanceRaw, observedDeployment]);
    return Object.freeze({ kind: "REDEEM_WITH_FEES_CURVE_V1", support: "PINNED_RPC_READ_ONLY",
      environment: bootstrap.environment, chainId, collection, sourceToken: source, curve, vault, receiver, factory, escrow,
      instanceId: instanceRaw as Hex, deploymentRevision, observedDeployment });
  } catch (error) {
    guard();
    if (error instanceof RedeemRouteError) throw error;
    throw contractReadError(error);
  }
}
